import { Drawer, Stack, Group, Text, Badge, Image, Divider, Box,
  SimpleGrid, ActionIcon, Tooltip, Tabs, Loader, Center, ScrollArea, UnstyledButton } from '@mantine/core'
import { useEffect, useState, useRef, type TouchEvent as ReactTouchEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useBreakpoint } from '../../hooks/useBreakpoint'
import {
  IconEdit, IconTool, IconArrowsLeftRight,
  IconHistory, IconQrcode, IconX, IconBox, IconShare, IconExternalLink,
  IconCopy, IconMapPin, IconBan, IconCircleCheck, IconTrash, IconChevronLeft,
  IconPlugConnected, IconPlugConnectedX,
} from '@tabler/icons-react'
import { getAssetHistory, getRepairsByAsset } from '../../api/client'
import { notifications } from '@mantine/notifications'
import { useNavigate } from 'react-router-dom'

const STATUS_COLOR: Record<string, string> = {
  AVAILABLE: 'teal', ASSIGNED: 'blue', BORROWED: 'yellow', INSTALLED: 'cyan',
  REPAIR: 'orange', DAMAGED: 'red', LOST: 'red', WRITE_OFF: 'gray',
}

// สีพื้นหลังหัว Drawer ตามสถานะ — เดิมกรมท่าเข้ม (#0f172a) เสมอไม่ว่าอุปกรณ์จะมีปัญหาหรือไม่
// เคสที่ต้องสนใจเป็นพิเศษ (ชำรุด/หาย/ตัดจำหน่าย/กำลังซ่อม) ควรรู้สึกต่างจากเคสปกติทันทีที่เปิดมาเห็น
const HEADER_BG: Record<string, string> = {
  DAMAGED: '#450a0a', LOST: '#450a0a', WRITE_OFF: '#1c1917',
  REPAIR: '#431407',
}

// ── กด tile แล้วมี feedback ยุบตัวเล็กน้อย (มือถือไม่มี hover ให้ใช้ :active แทน) — inline style
// ทำ :active ไม่ได้ ต้องใส่เป็น <style> scoped ด้วย class แทน ใส่ไว้ครั้งเดียวระดับโมดูล ──
const TILE_PRESS_STYLE = `
  .qv-tile { transition: transform 0.08s ease, background 0.08s ease; -webkit-tap-highlight-color: transparent; }
  .qv-tile:active { transform: scale(0.92); background: #f1f5f9 !important; }
  .qv-delete-tile { transition: transform 0.08s ease, background 0.08s ease; -webkit-tap-highlight-color: transparent; }
  .qv-delete-tile:active { transform: scale(0.97); background: #fee2e2 !important; }
  .qv-back-btn { transition: opacity 0.08s ease; -webkit-tap-highlight-color: transparent; }
  .qv-back-btn:active { opacity: 0.6; }
`

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <Box>
      <Text size="xs" c="dimmed" fw={500}>{label}</Text>
      <Text size="sm">{value || '—'}</Text>
    </Box>
  )
}

interface Props {
  asset: any | null
  opened: boolean
  onClose: () => void
  onEdit?: (asset: any) => void
  onClone?: (asset: any) => void
  onRepair?: (asset: any) => void
  onSwap?: (asset: any) => void
  onHistory?: (asset: any) => void
  onQR?: (asset: any) => void
  onLocation?: (asset: any) => void
  onLost?: (asset: any) => void
  onFound?: (asset: any) => void
  onInstall?: (asset: any) => void
  onUninstall?: (asset: any) => void
  onDelete?: (asset: any) => void
}

export default function AssetQuickView({ asset, opened, onClose, onEdit, onClone, onRepair, onSwap, onHistory, onQR, onLocation, onLost, onFound, onInstall, onUninstall, onDelete }: Props) {
  const { t } = useTranslation()
  const STATUS_LABEL: Record<string, string> = {
    AVAILABLE: t('assetStatus.available', 'Available'), ASSIGNED: t('assetStatus.assigned', 'Assigned'),
    BORROWED: t('assetStatus.borrowed', 'Borrowed'), INSTALLED: t('assetStatus.installed', 'Installed'),
    REPAIR: t('assetStatus.repair', 'In Repair'), DAMAGED: t('assetStatus.damaged', 'Damaged'),
    LOST: t('assetStatus.lost', 'Lost'), WRITE_OFF: t('assetStatus.writeOff', 'Written Off'),
  }
  const navigate = useNavigate()
  const { isMobile } = useBreakpoint()
  const [history, setHistory] = useState<any[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [repairJobs, setRepairJobs] = useState<any[]>([])
  const [loadingRepairs, setLoadingRepairs] = useState(false)
  const [imageExpanded, setImageExpanded] = useState(false)
  // ── ปัดขวาเพื่อปิด — เก็บจุดเริ่มแตะไว้ แล้วเช็คตอนปล่อยนิ้วว่าเป็นการปัดแนวนอนไปทางขวาจริงๆ
  // (ไม่ใช่การเลื่อนดูเนื้อหาแนวตั้งใน ScrollArea) ต้องปัดไกลพอ (>90px) และแนวนอนมากกว่าแนวตั้ง
  // อย่างน้อย 2 เท่า ถึงจะนับเป็นการปัดปิดจริง กันปิด Drawer โดยไม่ตั้งใจตอนแค่เลื่อนดูข้อมูล
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const handleTouchStart = (e: ReactTouchEvent) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }
  const handleTouchEnd = (e: ReactTouchEvent) => {
    if (!touchStartRef.current) return
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y
    if (dx > 90 && Math.abs(dx) > Math.abs(dy) * 2) onClose()
    touchStartRef.current = null
  }

  const handleShareAsset = async (a: any) => {
    // ใช้ window.location.origin เสมอ — จะได้ domain ปัจจุบัน (ptl.assetsservice.com ฯลฯ)
    const shareUrl = `${window.location.origin}/assets?asset=${encodeURIComponent(a.assetCode)}`
    const text = [
      `📦 ${a.name}`,
      `🏷 ${a.assetCode}`,
      a.serialNumber   ? `🔢 S/N: ${a.serialNumber}` : null,
      a.status         ? `📌 ${a.status}` : null,
      a.location       ? `📍 ${a.location}` : null,
      '',
      `🔗 ${shareUrl}`,
    ].filter(l => l !== null).join('\n')

    const shareData = { title: `AssetOS — ${a.assetCode}`, text, url: shareUrl }
    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData)
      } else {
        await navigator.clipboard.writeText(shareUrl)
        notifications.show({ color: 'teal', message: t('assets.urlCopied', 'Link copied') })
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        notifications.show({ color: 'red', message: t('assets.shareFailed', 'Share failed') })
      }
    }
  }

  const handleLineShare = (a: any) => {
    const shareUrl = `${window.location.origin}/assets?asset=${encodeURIComponent(a.assetCode)}`
    const text = `📦 ${a.name} (${a.assetCode})\n🔗 ${shareUrl}`
    const lineUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`
    window.open(lineUrl, '_blank', 'width=600,height=600')
  }

  const handleCopyLink = async (a: any) => {
    const shareUrl = `${window.location.origin}/assets?asset=${encodeURIComponent(a.assetCode)}`
    await navigator.clipboard.writeText(shareUrl)
    notifications.show({ color: 'teal', message: t('assets.urlCopied', 'Link copied') })
  }

  useEffect(() => {
    if (!asset || !opened) return
    setLoadingHistory(true)
    getAssetHistory(asset.id)
      .then(r => setHistory((r.data ?? []).slice(0, 8)))
      .catch(() => {})
      .finally(() => setLoadingHistory(false))

    setLoadingRepairs(true)
    getRepairsByAsset(asset.assetCode)
      .then(r => setRepairJobs(r.data ?? []))
      .catch(() => {})
      .finally(() => setLoadingRepairs(false))
  }, [asset?.id, opened])

  if (!asset) return null

  const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
  const fmtPrice = (p?: number | null) =>
    p ? `฿${Number(p).toLocaleString()}` : '—'

  const LineIcon = (size: number) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
    </svg>
  )

  // ── รายการปุ่มจัดการทั้งหมด — สร้างเป็น array ข้อมูลครั้งเดียว ให้ desktop (แถบไอคอนเดิม + tooltip)
  // กับ mobile (กริดปุ่มใหญ่ + label ใต้ไอคอน) ดึงไปแสดงผลคนละแบบจากชุดข้อมูลเดียวกัน — เดิมโค้ด
  // เขียนเงื่อนไข show/hide ของปุ่มแต่ละอันซ้ำ 2 รอบจะเสี่ยงหลุด sync กันได้ง่าย
  // สำคัญ: บนมือถือไม่มี hover เลย ต่อให้มี Tooltip ก็ไม่มีทางเห็นข้อความก่อนกด — ปุ่มไอคอนเปล่าๆ
  // เล็กๆ 13 ปุ่มเรียงแถวเดียวแบบเดิมจึงกดยากและเดาไม่ออกว่าปุ่มไหนทำอะไรบนมือถือ ต้องมี label
  // ติดปุ่มไปเลย ไม่ใช่แค่ tooltip ที่ใช้ไม่ได้จริงบนอุปกรณ์สัมผัส
  const actions = [
    { key: 'edit', show: !!onEdit, label: t("common.edit","Edit"), color: 'gray',
      icon: (s: number) => <IconEdit size={s} />, onClick: () => { onClose(); onEdit!(asset) } },
    { key: 'clone', show: !!onClone, label: t("assets.cloneAsset","Clone Asset"), color: 'indigo',
      icon: (s: number) => <IconCopy size={s} />, onClick: () => { onClose(); onClone!(asset) } },
    { key: 'location', show: !!onLocation, label: t("assets.moveLocation","Move Location"), color: 'teal',
      icon: (s: number) => <IconMapPin size={s} />, onClick: () => { onClose(); onLocation!(asset) } },
    // เดิมโชว์เฉพาะ status ที่ยังไม่ได้ส่งซ่อม/ตัดจำหน่าย/ชำรุด — คงเงื่อนไขเดิมไว้ทั้งหมด
    { key: 'repair', show: !!onRepair && asset.status !== 'REPAIR' && asset.status !== 'WRITE_OFF' && asset.status !== 'DAMAGED',
      label: t("assets.sendToRepair","Send to Repair"), color: 'orange',
      icon: (s: number) => <IconTool size={s} />, onClick: () => { onClose(); onRepair!(asset) } },
    // เดิมโชว์เฉพาะ asset.status === 'AVAILABLE' — แต่ backend swapAssets() ไม่ได้จำกัด
    // สถานะเลย และตัว modal เองก็มีข้อความเตือนรองรับกรณีไม่ AVAILABLE อยู่แล้ว
    // (ดู SwapAssetModal.tsx: notAvailableWarning) เงื่อนไขนี้เลยทำให้ปุ่มหายไปสำหรับ
    // อุปกรณ์ส่วนใหญ่ทั้งที่ควรกดได้ ตัดออกให้ตรงกับพฤติกรรมจริงของระบบ
    { key: 'swap', show: !!onSwap, label: t("assets.swapAsset","Swap Asset"), color: 'teal',
      icon: (s: number) => <IconArrowsLeftRight size={s} />, onClick: () => { onClose(); onSwap!(asset) } },
    { key: 'history', show: !!onHistory, label: t("assets.movementHistory","Movement History"), color: 'violet',
      icon: (s: number) => <IconHistory size={s} />, onClick: () => { onClose(); onHistory!(asset) } },
    { key: 'qr', show: !!onQR, label: t("assets.qrCode","QR Code"), color: 'teal',
      icon: (s: number) => <IconQrcode size={s} />, onClick: () => { onClose(); onQR!(asset) } },
    { key: 'lost', show: !!onLost && asset.status !== 'LOST' && asset.status !== 'WRITE_OFF',
      label: t("assets.reportLost","Report Lost"), color: 'grape',
      icon: (s: number) => <IconBan size={s} />, onClick: () => { onClose(); onLost!(asset) } },
    { key: 'found', show: !!onFound && asset.status === 'LOST', label: t("assets.markAsFound","Mark as Found"), color: 'teal',
      icon: (s: number) => <IconCircleCheck size={s} />, onClick: () => { onClose(); onFound!(asset) } },
    { key: 'install', show: !!onInstall && asset.status === 'AVAILABLE', label: t("assets.markInstalled","Mark as Installed"), color: 'cyan',
      icon: (s: number) => <IconPlugConnected size={s} />, onClick: () => { onClose(); onInstall!(asset) } },
    { key: 'uninstall', show: !!onUninstall && asset.status === 'INSTALLED', label: t("assets.uninstallBtn","Uninstall"), color: 'gray',
      icon: (s: number) => <IconPlugConnectedX size={s} />, onClick: () => { onClose(); onUninstall!(asset) } },
    { key: 'lineShare', show: true, label: t("assets.shareViaLine","Share via LINE"), color: 'green',
      icon: (s: number) => LineIcon(s), onClick: () => handleLineShare(asset) },
    { key: 'copyLink', show: true, label: t("assets.copyLink","Copy Link"), color: 'gray',
      icon: (s: number) => <IconCopy size={s} />, onClick: () => handleCopyLink(asset) },
    { key: 'share', show: true, label: t("common.share","Share"), color: 'cyan',
      icon: (s: number) => <IconShare size={s} />, onClick: () => handleShareAsset(asset) },
    // ปุ่ม Open in Assets — แสดงเสมอโดยเฉพาะในหน้าที่ไม่ใช่ Assets page
    { key: 'openInAssets', show: !onEdit, label: t("assets.openInAssetsPage","Open in Assets page"), color: 'indigo',
      icon: (s: number) => <IconExternalLink size={s} />,
      onClick: () => { onClose(); navigate(`/assets?asset=${encodeURIComponent(asset.assetCode)}&force=1`) } },
  ] as const
  // Delete แยกไว้ต่างหากเสมอ (ไม่รวมใน array ข้างบน) — ทั้ง desktop และ mobile ต้องแยกตำแหน่งให้
  // เห็นชัดว่าเป็นการกระทำที่ต่างระดับความเสี่ยงจากปุ่มอื่น กันกดพลาด
  const deleteAction = onDelete
    ? { label: t("assets.deletePermanentlyAdmin","Delete Permanently (ADMIN)"), onClick: () => { onClose(); onDelete(asset) } }
    : null
  const visibleActions = actions.filter(a => a.show)

  return (
    <>
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={isMobile ? '100%' : 420}
      withCloseButton={false}
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' } }}
    >
      <style>{TILE_PRESS_STYLE}</style>
      <div
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
      >
      {/* Header — มือถือใช้ปุ่ม "← Back" ขนาดใหญ่แทน X เล็กๆ มุมขวา ให้ความรู้สึกเหมือนเปิดหน้าใหม่
      เต็มจอแล้วกดย้อนกลับได้ ตรงกับที่ขอมา ไม่ใช่แค่ปิด popup
      พื้นหลังเปลี่ยนสีตามสถานะ — เคสมีปัญหา (ชำรุด/หาย/ตัดจำหน่าย/กำลังซ่อม) ควรรู้สึกต่างจากปกติ
      ทันทีที่เปิดมาเห็น ไม่ใช่กรมท่าเข้มเหมือนกันหมดไม่ว่าจะเป็นเคสไหน */}
      <Box p="md" style={{ background: HEADER_BG[asset.status] ?? '#0f172a', flexShrink: 0, transition: 'background 0.2s ease' }}>
        <Group justify="space-between" mb={6}>
          {isMobile ? (
            <UnstyledButton className="qv-back-btn" onClick={onClose} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <IconChevronLeft size={22} color="#fff" />
              <Text size="sm" c="white" fw={500}>{t('common.back', 'Back')}</Text>
            </UnstyledButton>
          ) : (
            <Group gap={8}>
              <Badge color={STATUS_COLOR[asset.status] ?? 'gray'} variant="filled" size="sm">
                {STATUS_LABEL[asset.status] ?? asset.status}
              </Badge>
              <Text size="xs" c="gray.4" ff="monospace">{asset.assetCode}</Text>
            </Group>
          )}
          {!isMobile && (
            <ActionIcon variant="subtle" color="gray" onClick={onClose} size="sm">
              <IconX size={15} />
            </ActionIcon>
          )}
        </Group>
        {isMobile && (
          <Group gap={8} mb={6}>
            <Badge color={STATUS_COLOR[asset.status] ?? 'gray'} variant="filled" size="sm">
              {STATUS_LABEL[asset.status] ?? asset.status}
            </Badge>
            <Text size="xs" c="gray.4" ff="monospace">{asset.assetCode}</Text>
          </Group>
        )}
        <Text fw={700} size="lg" c="white" lineClamp={2}>{asset.name}</Text>
        <Text size="xs" c="gray.4" mt={2}>
          {[asset.category, asset.subCategory].filter(Boolean).join(' / ')}
        </Text>
      </Box>

      {/* Action bar */}
      {isMobile ? (
        // ── มือถือ: กริดปุ่มใหญ่ ไอคอน 22px + label เห็นตรงๆ ใต้ไอคอนเสมอ ไม่ต้องพึ่ง tooltip
        // (ซึ่งใช้งานจริงบนจอสัมผัสไม่ได้อยู่แล้ว) ทัชได้ง่ายกว่าไอคอนเล็ก 14px แถวเดียวเดิมมาก ──
        <Box px="sm" py="sm" style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
          <SimpleGrid cols={4} spacing={6}>
            {visibleActions.map(a => (
              <UnstyledButton key={a.key} className="qv-tile" onClick={a.onClick}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  padding: '9px 4px', borderRadius: 12, background: '#fff', border: '1px solid #e2e8f0',
                }}>
                <ActionIcon variant="light" color={a.color} size={34} radius="xl" style={{ pointerEvents: 'none' }}>
                  {a.icon(17)}
                </ActionIcon>
                <Text size="9px" ta="center" lh={1.15} c="dark.4" fw={500}>{a.label}</Text>
              </UnstyledButton>
            ))}
          </SimpleGrid>
          {deleteAction && (
            <UnstyledButton className="qv-delete-tile" onClick={deleteAction.onClick}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                marginTop: 8, padding: '10px 4px', borderRadius: 12, background: '#fef2f2', border: '1px solid #fecaca', width: '100%',
              }}>
              <IconTrash size={16} color="#dc2626" />
              <Text size="xs" c="red" fw={600}>{deleteAction.label}</Text>
            </UnstyledButton>
          )}
        </Box>
      ) : (
        <Box px="md" py={8} style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
          <Group gap={6}>
            {visibleActions.map(a => (
              <Tooltip key={a.key} label={a.label} withArrow>
                <ActionIcon variant="light" color={a.color} size="sm" onClick={a.onClick}>
                  {a.icon(14)}
                </ActionIcon>
              </Tooltip>
            ))}
            {deleteAction && (
              <>
                <Box style={{ flex: 1 }} />
                <Tooltip label={deleteAction.label} withArrow color="red">
                  <ActionIcon variant="subtle" color="red" size="sm" onClick={deleteAction.onClick}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
        </Box>
      )}

      {/* Tabs */}
      <Tabs defaultValue="info" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Tabs.List px="md" style={{ flexShrink: 0 }}>
          <Tabs.Tab value="info" fz="xs">{t("license.tabInfo","Info")}</Tabs.Tab>
          <Tabs.Tab value="purchase" fz="xs">{t("common.purchase","Purchase")}</Tabs.Tab>
          <Tabs.Tab value="repair" fz="xs">Repair{repairJobs.length > 0 ? ` (${repairJobs.length})` : ''}</Tabs.Tab>
          <Tabs.Tab value="history" fz="xs">{t("common.history","History")}</Tabs.Tab>
        </Tabs.List>

        {/* Info */}
        <Tabs.Panel value="info" style={{ flex: 1, overflow: 'hidden' }}>
          <ScrollArea h="100%" p="md">
            <Stack gap="md">
              {(asset.imageUrl || asset.modelImageUrl) && (
                <Box style={{ position: 'relative', cursor: 'zoom-in' }} onClick={() => setImageExpanded(true)}>
                  <Image
                    src={asset.imageUrl || asset.modelImageUrl}
                    h={160} fit="contain" radius="md"
                    style={{ border: '1px solid #e2e8f0', background: '#f8fafc' }}
                  />
                  {isMobile && (
                    <Badge size="xs" color="dark" variant="filled" style={{ position: 'absolute', bottom: 8, right: 8, opacity: 0.75 }}>
                      🔍 {t('assets.tapToExpand', 'Tap to expand')}
                    </Badge>
                  )}
                </Box>
              )}
              <SimpleGrid cols={2} spacing="sm">
                <Field label={t("common.serialNumber","Serial Number")} value={asset.serialNumber} />
                <Field label={t("common.poNumber","PO Number")} value={asset.poNumber} />
                <Field label={t("common.department","Department")} value={asset.departmentCode} />
                <Field label={t("common.location","Location")} value={asset.location} />
                <Field label={t("common.ownerUser","Owner / User")} value={asset.owner} />
                <Field label={t("common.brand","Brand")} value={asset.brand} />
                <Field label={t("common.model","Model")} value={asset.model} />
                <Field label={t("common.vendor","Vendor")} value={asset.vendor} />
              </SimpleGrid>
              {asset.notes && (
                <>
                  <Divider />
                  <Field label={t("common.notes","Notes")} value={asset.notes} />
                </>
              )}
            </Stack>
          </ScrollArea>
        </Tabs.Panel>

        {/* Purchase */}
        <Tabs.Panel value="purchase" style={{ flex: 1, overflow: 'hidden' }}>
          <ScrollArea h="100%" p="md">
            <SimpleGrid cols={2} spacing="sm">
              <Field label={t("license.purchaseDate","Purchase Date")} value={fmtDate(asset.purchaseDate)} />
              <Field label={t("common.exclVat","Excl. VAT")} value={fmtPrice(asset.purchasePrice)} />
              <Field label={t("common.inclVat","Incl. VAT")} value={fmtPrice(asset.purchasePriceVat)} />
              <Box>
                <Text size="xs" c="dimmed" fw={500}>{t("common.warranty","Warranty")}</Text>
                {asset.warrantyLifetime
                  ? <Badge size="sm" color="teal" variant="light">Lifetime ♾️</Badge>
                  : <Text size="sm">{fmtDate(asset.warrantyStart)} → {fmtDate(asset.warrantyEnd)}</Text>
                }
              </Box>
            </SimpleGrid>
          </ScrollArea>
        </Tabs.Panel>

        {/* Repair History */}
        <Tabs.Panel value="repair" style={{ flex: 1, overflow: 'hidden' }}>
          <ScrollArea h="100%" p="md">
            {loadingRepairs ? (
              <Center p="xl"><Loader size="sm" color="teal" /></Center>
            ) : repairJobs.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="xl">{t("assets.noRepairJobsFound","No repair jobs found")}</Text>
            ) : (
              <Stack gap={10}>
                {repairJobs.map((j: any) => {
                  const statusColor: Record<string, string> = {
                    OPEN: 'orange', COMPLETED: 'teal', REPLACED: 'violet', WRITTEN_OFF: 'red',
                  }
                  return (
                    <Box key={j.id} p="sm" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <Group justify="space-between" mb={6}>
                        <Badge size="xs" color={statusColor[j.status] ?? 'gray'} variant="light">{j.status}</Badge>
                        <Text size="xs" c="dimmed">{fmtDate(j.createdAt)}</Text>
                      </Group>
                      <Text size="xs" c="dimmed" fw={500} mb={2}>{t("assets.problemSymptom","Problem / Symptom")}</Text>
                      <Text size="sm" mb={6}>{j.problem || '—'}</Text>

                      {j.type === 'EXTERNAL' && j.vendorName && (
                        <Text size="xs" c="dimmed">Vendor: <Text span c="dark">{j.vendorName}</Text></Text>
                      )}
                      {j.type === 'ON_SITE' && j.technicianName && (
                        <Text size="xs" c="dimmed">Technician: <Text span c="dark">{j.technicianName}</Text></Text>
                      )}
                      {j.type === 'INTERNAL' && Array.isArray(j.consumablesUsed) && j.consumablesUsed.length > 0 && (
                        <Box mt={4}>
                          <Group gap={4} mb={2}>
                            <IconBox size={12} color="#64748b" />
                            <Text size="xs" c="dimmed" fw={500}>{t("assets.partsUsed","Parts Used")}</Text>
                          </Group>
                          <Group gap={4}>
                            {j.consumablesUsed.map((c: any, ci: number) => (
                              <Badge key={ci} size="xs" color="grape" variant="light">
                                {c.name} × {c.qty}
                              </Badge>
                            ))}
                          </Group>
                        </Box>
                      )}
                      {(j.estimatedCost || j.actualCost) && (
                        <Text size="xs" c="dimmed">
                          Cost: {j.actualCost ? `฿${Number(j.actualCost).toLocaleString()} (actual)` : `฿${Number(j.estimatedCost).toLocaleString()} (estimated)`}
                        </Text>
                      )}
                      {j.resolution && (
                        <>
                          <Text size="xs" c="dimmed" fw={500} mt={6}>{t("assets.resolution","Resolution")}</Text>
                          <Text size="sm">{j.resolution}</Text>
                        </>
                      )}
                      {j.status === 'WRITTEN_OFF' && j.writeOffReason && (
                        <>
                          <Text size="xs" c="red" fw={500} mt={6}>{t("assets.writeOffReason","Write-Off Reason")}</Text>
                          <Text size="sm">{j.writeOffReason}</Text>
                        </>
                      )}
                      {j.note && <Text size="xs" c="dimmed" fs="italic" mt={6}>{j.note}</Text>}
                    </Box>
                  )
                })}
              </Stack>
            )}
          </ScrollArea>
        </Tabs.Panel>

        {/* History */}
        <Tabs.Panel value="history" style={{ flex: 1, overflow: 'hidden' }}>
          <ScrollArea h="100%" p="md">
            {loadingHistory ? (
              <Center p="xl"><Loader size="sm" color="teal" /></Center>
            ) : history.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="xl">{t("assets.noMovementHistory","No movement history")}</Text>
            ) : (
              <Stack gap={8}>
                {history.map((h: any, i: number) => (
                  <Box key={i} p="xs" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <Group justify="space-between" mb={2}>
                      <Badge size="xs" color="gray" variant="light" tt="capitalize">
                        {h.type?.replace(/_/g, ' ')}
                      </Badge>
                      <Text size="xs" c="dimmed">{fmtDate(h.createdAt)}</Text>
                    </Group>
                    {(h.fromValue || h.toValue) && (
                      <Text size="xs">{h.fromValue || '—'} → {h.toValue || '—'}</Text>
                    )}
                    {h.performedBy && <Text size="xs" c="dimmed">By: {h.performedBy}</Text>}
                    {h.note && <Text size="xs" c="dimmed" fs="italic">{h.note}</Text>}
                  </Box>
                ))}
              </Stack>
            )}
          </ScrollArea>
        </Tabs.Panel>
      </Tabs>
      </div>
    </Drawer>

    {/* ── Image Lightbox — แตะรูปในแท็บ Info เพื่อดูขยายเต็มจอ เดิมดูได้แค่กรอบ 160px เล็กๆ
    เท่านั้น แตะซ้ำหรือกดปุ่ม X เพื่อปิด — z-index สูงกว่า Drawer เอง เพื่อให้ลอยทับอยู่ด้านบนสุด ── */}
    {imageExpanded && (asset.imageUrl || asset.modelImageUrl) && (
      <Box
        onClick={() => setImageExpanded(false)}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.92)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out',
        }}>
        <ActionIcon variant="subtle" color="gray" size="lg"
          style={{ position: 'absolute', top: 16, right: 16 }}
          onClick={(e) => { e.stopPropagation(); setImageExpanded(false) }}>
          <IconX size={22} color="#fff" />
        </ActionIcon>
        <Image
          src={asset.imageUrl || asset.modelImageUrl}
          fit="contain"
          style={{ maxWidth: '92vw', maxHeight: '85vh' }}
          onClick={(e) => e.stopPropagation()}
        />
      </Box>
    )}
    </>
  )
}
